import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Play, ImageIcon, FileText, Download } from "lucide-react";

export default function EventDetail() {
  const { eid } = useParams();
  const [event, setEvent] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get(`/events/${eid}`).then(({ data }) => setEvent(data)).catch(() => setEvent({ error: true })); }, [eid]);

  if (!event) return <div className="p-10 text-center text-[#6E6259]">Loading...</div>;
  if (event.error) return <div className="p-10 text-center text-[#DC2626]">Event not found</div>;

  const media = event.media || [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/events" className="inline-flex items-center gap-1 text-[#5C061C] mb-4" data-testid="event-back">
        <ArrowLeft className="h-4 w-4" /> All events
      </Link>

      <div className="card-warm rounded-3xl overflow-hidden">
        {event.image_url && <img src={event.image_url} alt={event.title} className="w-full h-72 object-cover" />}
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            {event.is_live && <span className="pill-live rounded-full px-3 py-1 text-xs font-bold uppercase">● LIVE</span>}
          </div>
          <h1 className="font-serif text-4xl text-[#5C061C]">{event.title}</h1>
          <p className="mt-3 text-[#6E6259] leading-relaxed">{event.description}</p>
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#5C061C]">Gallery</h2>
          <Badge className="bg-[#5C061C]/10 text-[#5C061C]">{media.length} items</Badge>
        </div>
        {media.length === 0 ? (
          <div className="card-warm rounded-2xl p-10 text-center">
            <ImageIcon className="h-10 w-10 text-[#B45309] mx-auto opacity-40" />
            <p className="mt-3 text-[#6E6259]">The trustee hasn't added any photos or videos for this event yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {media.map((m) => (
              <div key={m.id} className="card-warm rounded-xl overflow-hidden group relative aspect-square" data-testid={`gallery-item-${m.id}`}>
                {m.kind === "pdf" ? (
                  <a href={m.url} target="_blank" rel="noopener noreferrer" className="w-full h-full bg-gradient-to-br from-[#F4EEDF] to-[#F9E4B7] grid place-items-center p-4 hover:from-[#F9E4B7] hover:to-[#F4EEDF] transition">
                    <FileText className="h-10 w-10 text-[#5C061C]" />
                    <span className="mt-2 text-xs font-semibold text-[#5C061C] inline-flex items-center gap-1"><Download className="h-3 w-3" /> Open PDF</span>
                  </a>
                ) : (
                  <button onClick={() => setSelected(m)} className="w-full h-full block">
                    {m.kind === "image" ? (
                      <img src={m.url} alt={m.caption || ""} className="w-full h-full object-cover group-hover:scale-105 transition" />
                    ) : (
                      <div className="w-full h-full bg-[#1F1A17] grid place-items-center relative">
                        <video src={m.url} className="w-full h-full object-cover opacity-70" muted playsInline />
                        <div className="absolute inset-0 grid place-items-center">
                          <div className="h-14 w-14 rounded-full bg-white/90 grid place-items-center"><Play className="h-6 w-6 text-[#5C061C]" /></div>
                        </div>
                      </div>
                    )}
                  </button>
                )}
                {m.caption && <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1.5 truncate">{m.caption}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="gallery-lightbox">
          <div className="max-w-5xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {selected.kind === "image" ? (
              <img src={selected.url} alt="" className="max-h-[85vh] mx-auto rounded-xl" />
            ) : (
              <video src={selected.url} controls autoPlay className="max-h-[85vh] w-full rounded-xl bg-black" />
            )}
            {selected.caption && <p className="text-center text-white mt-3">{selected.caption}</p>}
            <div className="text-center mt-3"><Button variant="outline" onClick={() => setSelected(null)} className="bg-white/90">Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}
